/**
 * The three store shapes every non-book channel needs.
 *
 * They are deliberately generic over the payload. Phase 2 is transport: it moves
 * frames, orders them, deduplicates them and publishes them once per animation
 * frame. What a `price_cents` means is the market page's problem, and a transport
 * that reached into payloads to reshape them would be the second wire format
 * docs/04 §9.2 exists to prevent.
 *
 * - `KeyedStore` — upsert by id, for `orders`, `positions` and `candles`. Newest
 *   wins; the version comparison itself lives in `dedupe.ts`, because the same
 *   rule has to gate the buffer as well as the store.
 * - `LogStore` — bounded append, newest first, for `trades`, `fills`,
 *   `settlements` and `notifications`. Bounded because a tab left open for an
 *   afternoon at 25 trades a second is 90,000 rows nobody will scroll to.
 *   `fills` passes a `keyOf` and so revises in place; the other three append,
 *   because their entries are facts that never change (§F31).
 * - `SingletonStore` — one object, replaced whole, for `balance`, `market`,
 *   `ticker`, `index` and `series`. `balance` in particular is **always a
 *   complete object, never a field delta**: a five-component balance delivered in
 *   pieces will, on the first dropped or duplicated frame, render
 *   `available + locked ≠ equity`, which is the single most alarming thing a
 *   custodial venue can display.
 */

import { ExternalStore } from './store'

// ---------------------------------------------------------------------------

export interface KeyedState<T> {
  byId: Map<string, T>
  /** Insertion order of first sight, so a list renders stably while rows update
   *  in place. */
  order: string[]
}

export class KeyedStore<T = unknown> {
  readonly store: ExternalStore<KeyedState<T>>
  readonly #keyOf: (value: T) => string | null

  constructor(keyOf: (value: T) => string | null) {
    this.#keyOf = keyOf
    this.store = new ExternalStore<KeyedState<T>>({ byId: new Map(), order: [] })
  }

  get size(): number {
    return this.store.state.byId.size
  }

  get(id: string): T | undefined {
    return this.store.state.byId.get(id)
  }

  /** Rows in first-seen order. Allocates, so it is a render-time call and not a
   *  per-frame one. */
  list(): T[] {
    const state = this.store.state
    const out: T[] = []
    for (const id of state.order) {
      const value = state.byId.get(id)
      if (value !== undefined) out.push(value)
    }
    return out
  }

  upsert(value: T): boolean {
    const id = this.#keyOf(value)
    if (id === null) return false
    const state = this.store.state
    if (!state.byId.has(id)) state.order.push(id)
    state.byId.set(id, value)
    this.store.touch()
    return true
  }

  remove(id: string): boolean {
    const state = this.store.state
    if (!state.byId.delete(id)) return false
    const at = state.order.indexOf(id)
    if (at >= 0) state.order.splice(at, 1)
    this.store.touch()
    return true
  }

  /** Seed from a REST snapshot. The socket will redeliver these; `dedupe.ts` is
   *  what recognises that, and it is seeded from the same list. */
  seed(values: readonly T[]): void {
    for (const value of values) this.upsert(value)
  }

  clear(): void {
    this.store.set({ byId: new Map(), order: [] })
  }
}

// ---------------------------------------------------------------------------

export interface LogState<T> {
  /** Newest first — the order every tape, fill list and notification tray
   *  renders in, so no component has to reverse it per frame. */
  entries: T[]
  /** Rows dropped off the tail because the log is bounded. Not a loss of data
   *  the user can act on: the REST endpoint is the archive. */
  evicted: number
}

export const DEFAULT_LOG_CAPACITY = 500

export class LogStore<T = unknown> {
  readonly store: ExternalStore<LogState<T>>
  readonly #capacity: number
  /** Set only for a log whose entries can be revised — `fills`, since §F31.
   *  `null` for `trades`, `settlements` and `notifications`, which append. */
  readonly #keyOf: ((value: T) => string | null) | null

  constructor(capacity = DEFAULT_LOG_CAPACITY, keyOf: ((value: T) => string | null) | null = null) {
    this.#capacity = capacity
    this.#keyOf = keyOf
    this.store = new ExternalStore<LogState<T>>({ entries: [], evicted: 0 })
  }

  get size(): number {
    return this.store.state.entries.length
  }

  get entries(): readonly T[] {
    return this.store.state.entries
  }

  /**
   * Append one fact — or revise one, for a log constructed with a `keyOf`.
   *
   * **A trade is never revised. A fill is, and §F31 is where that changed.**
   * The public tape appends and never upserts: a trade is a public fact about a
   * price and there is no second frame for it. A *fill* has two tiers under
   * D-B1 — matched at the sequencer, then mined when its `matchOrders`
   * transaction is included on Arbitrum — and the second arrives as the same
   * `fill_id` at a higher `version` with `settlement_tx_hash` populated.
   *
   * A revision replaces the entry **in place**, keeping its position in the
   * tape. It must not jump to the top: `executed_at_ms` did not change, only
   * what we know about the transaction that settled it, and a row that
   * reorders itself under the user's cursor is a worse lie than a missing tx
   * hash.
   *
   * Ordering is `dedupe.ts`'s job, not this one. By the time a frame reaches
   * here it has already been checked to be strictly newer, so this method never
   * compares versions — the same division of labour `KeyedStore` has.
   */
  append(value: T): void {
    const state = this.store.state
    const id = this.#keyOf?.(value) ?? null
    if (id !== null) {
      for (let i = 0; i < state.entries.length; i += 1) {
        if (this.#keyOf?.(state.entries[i]!) === id) {
          state.entries[i] = value
          this.store.touch()
          return
        }
      }
    }
    state.entries.unshift(value)
    if (state.entries.length > this.#capacity) {
      state.entries.length = this.#capacity
      state.evicted += 1
    }
    this.store.touch()
  }

  appendAll(values: readonly T[]): void {
    for (const value of values) this.append(value)
  }

  /** Seed from a REST snapshot, which arrives newest-first already. */
  seed(values: readonly T[]): void {
    const state = this.store.state
    state.entries = [...values].slice(0, this.#capacity)
    this.store.touch()
  }

  clear(): void {
    this.store.set({ entries: [], evicted: 0 })
  }
}

// ---------------------------------------------------------------------------

export class SingletonStore<T = unknown> {
  readonly store: ExternalStore<T | null>

  constructor(initial: T | null = null) {
    this.store = new ExternalStore<T | null>(initial)
  }

  get value(): T | null {
    return this.store.state
  }

  set(value: T): void {
    this.store.set(value)
  }

  clear(): void {
    this.store.set(null)
  }
}
